import { useQuery } from '@apollo/client';

import { SaleDocumentType } from '__generated__/globalTypes';

import {
  SaleAcknowledgementDocument,
  SaleAcknowledgementDocumentVariables,
} from 'query/__generated__/saleAcknowledgementDocument';
import { SALE_ACKNOWLEDGEMENT_DOCUMENT_QUERY } from 'query/saleAcknowledgementDocument';

import {
  CONFIDENTIALITY_AGREEMENT,
  ClickThroughDocument,
  ClickThroughDocumentType,
} from './saleDocuments';

export type ClickThroughDocumentState =
  | { status: 'ready'; document: ClickThroughDocument }
  | { status: 'loading' }
  | { status: 'failed'; retry: () => void };

// The CA from the bundle; the PEC summary and the bid notice from api. `skip` leaves an
// api-served document unfetched, reported as loading.
export function useClickThroughDocument(
  saleId: string,
  docType: ClickThroughDocumentType,
  skip = false,
): ClickThroughDocumentState {
  const servedByApi = docType !== SaleDocumentType.CA;
  const { data, loading, error, refetch } = useQuery<
    SaleAcknowledgementDocument,
    SaleAcknowledgementDocumentVariables
  >(SALE_ACKNOWLEDGEMENT_DOCUMENT_QUERY, {
    variables: { saleId, docType },
    skip: skip || !servedByApi,
    notifyOnNetworkStatusChange: true,
  });

  if (!servedByApi) {
    return { status: 'ready', document: CONFIDENTIALITY_AGREEMENT };
  }
  const served = data?.saleAcknowledgementDocument;
  if (served) {
    return {
      status: 'ready',
      document: {
        doc_type: docType,
        title: served.title,
        version: served.version,
        text: served.text,
        document_sha256: served.document_sha256,
      },
    };
  }
  if (skip || (loading && !error)) {
    return { status: 'loading' };
  }
  return {
    status: 'failed',
    // A refetch that fails again comes back through `error` as this same state.
    retry: () => {
      refetch().catch(() => undefined);
    },
  };
}
